import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { loginUser, postRequest, getRequest } from '../services/apiService'; // Import getRequest
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as THREE from 'three';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showAttendancePopup, setShowAttendancePopup] = useState(false); // New state for popup
  const [pendingLoginData, setPendingLoginData] = useState(null); // Store login data temporarily
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  // 3D animation elements
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const frameIdRef = useRef(null);

  // Function to get user ID from localStorage (can be moved to AuthContext or utility)
  const getUserId = (authData) => {
    try {
      if (authData && authData.user_id) {
        return authData.user_id;
      }
      // If user_id is not directly available, check if `user` object exists and has an `id`
      if (authData && authData.user && authData.user.id) {
        return authData.user.id;
      }
    } catch (error) {
      console.error("Error getting user ID from authData:", error);
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Simple validation
    if (!username || !password) {
      setError('Please enter both username and password');
      toast.error('Please enter both username and password');
      return;
    }

    try {
      // Get full response from loginUser
      const res = await loginUser({
        loginname: username,
        password: password,
      });

      // console.log('Login Response:', res.data);

      // Check if login succeeded based on status and if access token exists
      if (res.status === 200 && res.data?.access) {
        const userId = getUserId(res.data);
        if (!userId) {
          toast.error("User ID not found after login.");
          // Proceed with login even if user ID isn't found for attendance check
          login(res.data);
          if (rememberMe) {
            localStorage.setItem('authData', JSON.stringify(res.data));
          }
          navigate('/');
          return;
        }

        const today = new Date();
        const attendanceDate = today.toISOString().split('T')[0]; // YYYY-MM-DD

        try {
          const attendanceRes = await getRequest(`/config/attendance/`, {
            user: userId,
            attendance_date: attendanceDate
          });

          // console.log('Attendance Check Response:', attendanceRes.data);

          // Corrected: Access attendance records directly from attendanceRes.data
          // as per your console output, attendanceRes.data is already the array.
          const attendanceDataForToday = attendanceRes.data.find(
            (item) => item.attendance_date === attendanceDate && item.user.id === userId
          );

          if (attendanceDataForToday && attendanceDataForToday.check_in !== null && attendanceDataForToday.check_in !== '') {
            // Attendance is already marked, proceed with login directly
            // console.log('Attendance already marked for today. Skipping popup.');
            login(res.data);
            if (rememberMe) {
              localStorage.setItem('authData', JSON.stringify(res.data));
            }
            navigate('/');
          } else {
            // Attendance not marked or check_in is blank, show popup
            setPendingLoginData(res.data);
            setShowAttendancePopup(true);
          }
        } catch (attendanceErr) {
          console.error('Error fetching attendance:', attendanceErr);
          // If there's an error fetching attendance, still proceed with login and show popup as a fallback
          // Or decide based on business logic if you want to block login here
          setPendingLoginData(res.data);
          setShowAttendancePopup(true);
          toast.warn('Could not verify attendance status. Please confirm if you are the main user.');
        }

      } else {
        const errorMsg = 'Invalid credentials. Please try again.';
        setError(errorMsg);
        toast.error(errorMsg);
      }
    } catch (err) {
      console.error('Login Error:', err);
      let errorMsg = 'Login failed. Please try again.';
      if (err.response && err.response.data) {
        errorMsg = err.response.data.message || JSON.stringify(err.response.data.detail);
      }
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  // Handle attendance confirmation
  const handleAttendanceConfirmation = async (isMainUser) => {
    if (isMainUser && pendingLoginData) {
      const userId = getUserId(pendingLoginData);
      if (!userId) {
        toast.error("User ID not found for attendance marking.");
        console.error("User ID not found in pendingLoginData.");
        // Proceed with login even if attendance marking fails
      } else {
        const now = new Date();
        const attendanceDate = now.toISOString().split('T')[0]; // YYYY-MM-DD
        const isoTime = now.toISOString(); // ISO 8601 format

        const payload = {
          user: userId,
          attendance_date: attendanceDate,
          attendance_status: 1, // 1 for check-in
          check_in: isoTime,
          check_out: null, // Explicitly set to null for check-in
        };

        try {
          // Hit the attendance API directly from here
          await postRequest('/config/attendance/', payload);
          // console.log('Attendance marked successfully for main user');
          toast.success('Attendance marked successfully!');
        } catch (error) {
          console.error('Error marking attendance:', error.response?.data || error.message);
          const errorMessage = error.response?.data?.detail || error.message || 'Failed to mark attendance.';
          toast.error(errorMessage);
        }
      }
    } else {
      // console.log('User is not the main user, skipping attendance marking');
    }

    // Complete the login process
    login(pendingLoginData);
    if (rememberMe) {
      localStorage.setItem('authData', JSON.stringify(pendingLoginData));
    }

    // Close popup and navigate
    setShowAttendancePopup(false);
    setPendingLoginData(null);
    navigate('/');
  };

  // Handle popup close
  const handlePopupClose = () => {
    // If the user cancels the popup, still proceed with login but without marking attendance
    if (pendingLoginData) {
      login(pendingLoginData);
      if (rememberMe) {
        localStorage.setItem('authData', JSON.stringify(pendingLoginData));
      }
      navigate('/');
    }
    setShowAttendancePopup(false);
    setPendingLoginData(null);
  };

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Initialize Three.js scene
  useEffect(() => {
    if (!mountRef.current) return;

    // Setup
    const width = mountRef.current.clientWidth;
    const height = mountRef.current.clientHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
    camera.position.z = 20;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setClearColor(0xffffff, 0);
    renderer.setSize(width, height);
    mountRef.current.appendChild(renderer.domElement);

    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(0, 10, 10);
    scene.add(directionalLight);

    // Background particles
    const particlesGeometry = new THREE.BufferGeometry();
    const particlesCount = 200;
    const posArray = new Float32Array(particlesCount * 3);

    for (let i = 0; i < particlesCount * 3; i++) {
      posArray[i] = (Math.random() - 0.5) * 40;
    }

    particlesGeometry.setAttribute('position', new THREE.BufferAttribute(posArray, 3));
    const particlesMaterial = new THREE.PointsMaterial({
      size: 0.1,
      color: 0x4db6ac,
      transparent: true,
      opacity: 0.8
    });
    const particlesMesh = new THREE.Points(particlesGeometry, particlesMaterial);
    scene.add(particlesMesh);

    // Create chart geometries
    const charts = [];

    // Bar chart - light blue
    const createBarChart = () => {
      const group = new THREE.Group();
      const barColors = [0x67e8f9, 0x22d3ee, 0x06b6d4, 0x0891b2, 0x0e7490];

      for (let i = 0; i < 5; i++) {
        const height = Math.random() * 5 + 2;
        const geometry = new THREE.BoxGeometry(1, height, 1);
        const material = new THREE.MeshPhongMaterial({
          color: barColors[i % barColors.length],
          transparent: true,
          opacity: 0.8
        });
        const bar = new THREE.Mesh(geometry, material);
        bar.position.x = i * 1.5 - 3;
        bar.position.y = height / 2;
        group.add(bar);
      }

      group.position.set(-8, -5, 0);
      group.rotation.y = 0.2;
      return group;
    };

    // Pie chart - light pink/purple
    const createPieChart = () => {
      const group = new THREE.Group();
      const pieColors = [0xf9a8d4, 0xf472b6, 0xec4899, 0xdb2777];

      const radius = 3;
      let startAngle = 0;

      for (let i = 0; i < 4; i++) {
        const angle = Math.random() * Math.PI / 2 + Math.PI / 6;
        const geometry = new THREE.CylinderGeometry(radius, radius, 0.6, 32, 1, false, startAngle, angle);
        const material = new THREE.MeshPhongMaterial({
          color: pieColors[i % pieColors.length],
          transparent: true,
          opacity: 0.8
        });

        const segment = new THREE.Mesh(geometry, material);
        segment.rotation.x = Math.PI / 2;
        group.add(segment);

        startAngle += angle;
      }

      group.position.set(8, -3, 0);
      group.rotation.z = Math.PI / 6;
      return group;
    };

    // Line chart - light green
    const createLineChart = () => {
      const group = new THREE.Group();
      const points = [];

      for (let i = 0; i < 6; i++) {
        const x = i - 2.5;
        const y = Math.sin(i * 0.8) * 1.5 + Math.random() * 0.5;
        points.push(new THREE.Vector3(x, y, 0));
      }

      const lineGeometry = new THREE.BufferGeometry().setFromPoints(points);
      const lineMaterial = new THREE.LineBasicMaterial({ color: 0x4ade80, linewidth: 3 });
      const line = new THREE.Line(lineGeometry, lineMaterial);

      // Add spheres at data points
      points.forEach(point => {
        const sphereGeometry = new THREE.SphereGeometry(0.15, 16, 16);
        const sphereMaterial = new THREE.MeshPhongMaterial({ color: 0x22c55e });
        const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
        sphere.position.copy(point);
        group.add(sphere);
      });

      group.add(line);
      group.position.set(0, 4, 0);
      group.rotation.z = 0.1;
      return group;
    };

    // Donut chart - mixed colors
    const createDonutChart = () => {
      const group = new THREE.Group();
      const donutColors = [0x93c5fd, 0x60a5fa, 0x3b82f6, 0xddd6fe, 0xc4b5fd];
      const donutRadius = 2.5;
      const tubeRadius = 0.7;

      for (let i = 0; i < 5; i++) {
        const startAngle = i * (Math.PI * 2) / 5;
        const endAngle = startAngle + (Math.PI * 2) / 5 - 0.1;

        const curve = new THREE.CatmullRomCurve3([
          new THREE.Vector3(donutRadius * Math.cos(startAngle), donutRadius * Math.sin(startAngle), 0),
          new THREE.Vector3(donutRadius * Math.cos((startAngle + endAngle) / 2), donutRadius * Math.sin((startAngle + endAngle) / 2), 0),
          new THREE.Vector3(donutRadius * Math.cos(endAngle), donutRadius * Math.sin(endAngle), 0)
        ]);

        const geometry = new THREE.TubeGeometry(curve, 20, tubeRadius, 8, false);
        const material = new THREE.MeshPhongMaterial({
          color: donutColors[i % donutColors.length],
          transparent: true,
          opacity: 0.8
        });

        const tube = new THREE.Mesh(geometry, material);
        group.add(tube);
      }

      group.position.set(0, -3, 0);
      group.rotation.x = Math.PI / 6;
      return group;
    };

    // Add charts to scene
    const barChart = createBarChart();
    const pieChart = createPieChart();
    const lineChart = createLineChart();
    const donutChart = createDonutChart();

    scene.add(barChart);
    scene.add(pieChart);
    scene.add(lineChart);
    scene.add(donutChart);
    scene.add(particlesMesh);

    charts.push(barChart, pieChart, lineChart, donutChart);

    // Animation loop
    const animate = () => {
      charts.forEach(chart => {
        chart.rotation.y += 0.005;
      });

      // Bobbing motion
      barChart.position.y = -5 + Math.sin(Date.now() * 0.001) * 0.3;
      pieChart.position.y = -3 + Math.sin(Date.now() * 0.001 + 1) * 0.3;
      lineChart.position.y = 4 + Math.sin(Date.now() * 0.001 + 2) * 0.3;
      donutChart.position.y = -3 + Math.sin(Date.now() * 0.001 + 3) * 0.3;

      // Rotate particles slowly
      particlesMesh.rotation.y += 0.0005;

      renderer.render(scene, camera);
      frameIdRef.current = requestAnimationFrame(animate);
    };

    animate();
    sceneRef.current = { scene, camera, renderer, charts, particlesMesh };

    // Handle resize
    const handleResize = () => {
      if (!mountRef.current) return;

      const width = mountRef.current.clientWidth;
      const height = mountRef.current.clientHeight;

      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (frameIdRef.current) {
        cancelAnimationFrame(frameIdRef.current);
      }
      if (mountRef.current && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement);
      }
      charts.forEach(chart => {
        chart.traverse(obj => {
          if (obj.geometry) obj.geometry.dispose();
          if (obj.material) {
            if (Array.isArray(obj.material)) {
              obj.material.forEach(material => material.dispose());
            } else {
              obj.material.dispose();
            }
          }
        });
      });
      if (particlesMesh.geometry) particlesMesh.geometry.dispose();
      if (particlesMesh.material) particlesMesh.material.dispose();
    };
  }, []);

  return (
    <div className="flex flex-col md:flex-row h-screen bg-gray-50">
      {/* Toast Container for error popups */}
      <ToastContainer position="top-right" autoClose={5000} />

      {/* Attendance Confirmation Popup */}
      {showAttendancePopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="text-center">
              <div className="mb-4">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-teal-100">
                  <svg className="h-6 w-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Attendance Confirmation
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Do you want to mark your attendance for today?
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => handleAttendanceConfirmation(true)}
                  className="flex-1 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-md font-medium transition-colors duration-200"
                >
                  Yes
                </button>
                <button
                  onClick={() => handleAttendanceConfirmation(false)}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-md font-medium transition-colors duration-200"
                >
                  No
                </button>
              </div>
             
            </div>
          </div>
        </div>
      )}

      {/* Mobile View - Header with brand name */}
      <div className="md:hidden bg-teal-500 p-4 text-center text-white">
        <h1 className="text-xl font-bold">Project Management</h1>
        <p className="text-sm mt-1">Streamline your workflow</p>
      </div>

      {/* Left side - 3D Charts animation section (hidden on mobile) */}
      <div className="hidden md:flex md:w-1/2 lg:w-1/2 bg-gradient-to-br from-teal-100 to-cyan-50 flex-col justify-center items-center p-4 md:p-6 lg:p-8 text-teal-800 overflow-hidden relative">
        {/* 3D Charts container */}
        <div
          ref={mountRef}
          className="absolute inset-0"
        />

        {/* Logo */}
        <div className="absolute top-6 left-6 z-10">
          <div className="flex items-center">
            <div className="w-10 h-10 rounded-lg bg-teal-500 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <span className="ml-3 text-lg font-bold text-teal-700">PMS</span>
          </div>
        </div>

        {/* Text overlay */}
        <div className="z-10 max-w-md w-full text-center bg-white/70 backdrop-blur-sm p-6 rounded-xl shadow-lg">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-2 md:mb-3 break-words text-teal-700">
            Project Management Suite
          </h1>
          <p className="text-sm md:text-base lg:text-lg mb-3 md:mb-4 text-teal-600">
            Plan, track, and manage your projects efficiently with our intuitive tools and insightful analytics.
          </p>
          <div className="grid grid-cols-3 gap-4 mt-6">
            <div className="text-center">
              <div className="w-10 h-10 mx-auto rounded-full bg-teal-100 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <p className="text-xs mt-1 text-teal-700">Task Management</p>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 mx-auto rounded-full bg-blue-100 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <p className="text-xs mt-1 text-teal-700">Analytics</p>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 mx-auto rounded-full bg-green-100 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <p className="text-xs mt-1 text-teal-700">Team Collaboration</p>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile view - miniature 3D preview */}
      <div className="md:hidden p-4 bg-gradient-to-r from-teal-50 to-blue-50 h-48 relative">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-lg font-semibold text-teal-800">Project Management Suite</h2>
            <p className="text-sm text-teal-600">Visual analytics & tracking</p>
          </div>
        </div>
      </div>

      {/* Separator between sections - only visible on desktop */}
      <div className="hidden md:block w-1 bg-gradient-to-b from-teal-300 via-blue-300 to-teal-300 self-stretch relative z-10">
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white shadow-md flex items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
          </svg>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="w-full md:w-1/2 lg:w-1/2 flex items-center justify-center p-4 md:p-6 lg:p-8 bg-white">
        <div className="max-w-md w-full">
          <div className="text-center mb-4 md:mb-6 lg:mb-8">
            <h2 className="text-xl md:text-3xl lg:text-4xl font-bold text-teal-700">Welcome back</h2>
            <p className="mt-2 text-sm md:text-base lg:text-lg text-teal-600">Please sign in to your workspace</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3 md:space-y-4 lg:space-y-6">
            <div className="bg-white p-6 rounded-xl shadow-lg border border-teal-50">
              <div className="mb-4">
                <label htmlFor="username" className="block text-sm font-medium mb-1 text-teal-700">
                  Username
                </label>
                <div className="relative">
                  <input
                    id="username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value.toUpperCase())}
                    className="pl-10 w-full px-3 md:px-4 py-2 border border-teal-200 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white text-gray-800"
                    placeholder="username"
                    required
                  />
                </div>
              </div>

              <div className="mb-6">
                <div className="flex items-center justify-between mb-1">
                  <label htmlFor="password" className="block text-sm font-medium text-teal-700">
                    Password
                  </label>
                </div>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 w-full px-3 md:px-4 py-2 border border-teal-200 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white text-gray-800"
                    placeholder="••••••••"
                    required
                  />
                  {/* Show/Hide Password Icon Button */}
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-teal-600 hover:text-teal-800 cursor-pointer"
                    onClick={togglePasswordVisibility}
                  >
                    {showPassword ? (
                      // Eye icon for hide password
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      // Eye-off icon for show password
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                className="w-full py-2 px-4 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 transition duration-200 shadow-md"
              >
                Sign in
              </button>
            </div>

          </form>

        </div>
      </div>
    </div>
  );
};

export default Login;